/**
 * Sélecteur de responsables basé sur les tags sélectionnés.
 *
 * Responsabilités :
 * - définir la règle métier d'exclusion (utilisateur courant, déjà sélectionnés)
 * - déléguer fetch, options et sélection à useUtilisateurMultiSelect
 */
import { UserContext } from 'core/contexts/UserContext'
import { UtilisateurMultiSelect } from 'lauria/components/common/UtilisateurMultiSelect'
import { useUtilisateurMultiSelect } from 'lauria/hooks/useUtilisateurMultiSelect'
import {
  SelectedTag,
  SelectedUtilisateur,
} from 'lauria/types/selectedUtilisateur'
import React, { useCallback, useContext } from 'react'
import { FieldError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export type SelectedResponsable = SelectedUtilisateur

type ResponsableSelectProps = {
  selectedTags: SelectedTag[]
  selectedResponsables: SelectedResponsable[]
  setSelectedResponsables: React.Dispatch<
    React.SetStateAction<SelectedResponsable[]>
  >
  displayDefaultOption?: boolean
  width?: string
  error?: FieldError
}

export const ResponsableSelect: React.FC<ResponsableSelectProps> = ({
  selectedTags,
  selectedResponsables,
  setSelectedResponsables,
  displayDefaultOption = true,
  width = '[300px]',
  error,
}) => {
  const { t } = useTranslation()
  const { user: currentUser } = useContext(UserContext)
  const currentUserId = currentUser?.idUtilisateur ?? null

  const isUtilisateurExcluded = useCallback(
    (utilisateur: { idUtilisateur: number }) =>
      utilisateur.idUtilisateur === currentUserId ||
      selectedResponsables.some(
        (responsable) => responsable.id === utilisateur.idUtilisateur
      ),
    [currentUserId, selectedResponsables]
  )

  const { options, isLoading, selectValue, handleSelect } =
    useUtilisateurMultiSelect({
      selectedTags,
      onSelect: setSelectedResponsables,
      isUtilisateurExcluded,
    })

  return (
    <UtilisateurMultiSelect
      placeholder={t('applications.form.parameters.responsablesPlaceholder')}
      fieldName="responsables"
      options={options}
      isLoading={isLoading}
      value={selectValue}
      onChange={handleSelect}
      displayDefaultOption={displayDefaultOption}
      width={width}
      error={error}
    />
  )
}
